import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { GripVertical, ChevronUp } from "lucide-react";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { ComboBox } from "@/shared/components/ComboBox/ComboBox";
import type { SearchableField } from "@/shared/utils/smartSearch";
import type { Entry } from "@/shared/contexts/EntriesContext";
import { formatTime } from "./EntriesTable/EntriesTable";
import type { Matter } from "@/shared/cache";

// App icon imports (same set as EntriesTable)
import wordIcon from "../../../assets/applications/WordIcon.svg";
import adobeIcon from "../../../assets/applications/AdobeIcon.svg";
import outlookIcon from "../../../assets/applications/OutlookIcon.svg";
import powerpointIcon from "../../../assets/applications/PowerpointIcon.svg";
import excelIcon from "../../../assets/applications/ExcelIcon.svg";
import zoomIcon from "../../../assets/applications/ZoomIcon.svg";
import chromeIcon from "../../../assets/applications/ChromeIcon.svg";
import teamsIcon from "../../../assets/applications/TeamsIcon.svg";
import calendarIcon from "../../../assets/applications/CalendarIcon.svg";
import kofaxIcon from "../../../assets/applications/KofaxIcon.svg";
import foxitIcon from "../../../assets/applications/FoxitIcon.svg";
import photosIcon from "../../../assets/applications/WindowsPhotos.svg";
import combineIconImg from "../../../assets/applications/CombineIcon.svg";
import combineIconWhite from "../../../assets/applications/CombineIconWhite.svg";
import duplicateIcon from "../../../assets/applications/DuplicateIcon.svg";
import duplicateIconWhite from "../../../assets/applications/DuplicateIconWhite.svg";
import phoneIcon from "../../../assets/icons/phone.svg";
import phoneWhiteIcon from "../../../assets/icons/phoneWhite.svg";
import userInputIcon from "../../../assets/icons/userInput.svg";
import userInputWhiteIcon from "../../../assets/icons/userInputWhite.svg";

interface CombineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: Entry[];
  matters: Matter[];
  onConfirm: (ids: number[], matterId: string, mainEntryId: number) => Promise<void> | void;
  onCancel: () => void;
}

function useAppIcon(darkMode: boolean) {
  return (application?: string) => {
    switch (application) {
      case "Microsoft Word": return wordIcon;
      case "Microsoft Outlook": return outlookIcon;
      case "Microsoft Outlook Calendar": return calendarIcon;
      case "Adobe Acrobat": return adobeIcon;
      case "Microsoft Powerpoint": return powerpointIcon;
      case "Microsoft Excel": return excelIcon;
      case "Zoom": return zoomIcon;
      case "Google Chrome": return chromeIcon;
      case "Microsoft Teams": return teamsIcon;
      case "Phone": return darkMode ? phoneWhiteIcon : phoneIcon;
      case "User Input": return darkMode ? userInputWhiteIcon : userInputIcon;
      case "Kofax": return kofaxIcon;
      case "Foxit": return foxitIcon;
      case "Combine": return darkMode ? combineIconWhite : combineIconImg;
      case "Duplicate": return darkMode ? duplicateIconWhite : duplicateIcon;
      case "Photos": return photosIcon;
      default: return null;
    }
  };
}

// Sortable entry row component
function SortableEntryRow({
  entry,
  isMain,
  onPromote,
  darkMode,
  getIcon,
}: {
  entry: Entry;
  isMain: boolean;
  onPromote: () => void;
  darkMode: boolean;
  getIcon: (app?: string) => string | null;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  // Lock drag to vertical axis only — zero out X to prevent horizontal scroll
  const yOnlyTransform = transform ? { ...transform, x: 0 } : transform;

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(yOnlyTransform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
    borderBottom: "1px solid color-mix(in oklch, var(--border) 30%, transparent)",
    ...(isMain
      ? {
          borderLeft: "3px solid var(--brand-dark-green)",
          backgroundColor: darkMode
            ? "var(--card)"
            : "color-mix(in oklch, var(--brand-dark-green) 8%, white)",
        }
      : { borderLeft: "3px solid transparent" }),
  };

  const icon = getIcon(entry.application);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center gap-3 px-4 py-2.5 text-[13px] min-h-[48px] cursor-default touch-none select-none"
    >
      {/* Left slot — "Main" label or promote arrow, fixed width for alignment */}
      <div className="flex-shrink-0 w-10 flex items-center justify-center">
        {isMain ? (
          <span
            className="text-[10px] font-semibold rounded px-1 py-0.5"
            style={{
              color: darkMode
                ? "var(--brand-light-blue)"
                : "var(--brand-dark-green)",
              backgroundColor: darkMode
                ? "color-mix(in oklch, var(--brand-light-blue) 15%, transparent)"
                : "color-mix(in oklch, var(--brand-dark-green) 20%, transparent)",
            }}
          >
            Main
          </span>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPromote();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                onPromote();
              }
            }}
            className={`p-0.5 rounded transition-colors ${
              darkMode
                ? "hover:bg-white/10 text-gray-500 hover:text-gray-300 focus:ring-1 focus:ring-white/30 focus:outline-none"
                : "hover:bg-gray-200 text-gray-400 hover:text-gray-600 focus:ring-1 focus:ring-gray-400 focus:outline-none"
            }`}
            title="Set as main entry"
          >
            <ChevronUp size={14} />
          </button>
        )}
      </div>

      {/* Drag grip indicator (visual only) */}
      <GripVertical
        size={14}
        className={`flex-shrink-0 ${darkMode ? "text-gray-600" : "text-gray-300"}`}
      />

      {/* App icon */}
      <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
        {icon && <img src={icon} alt="" className="w-7 h-7" />}
      </div>

      {/* Date + end time */}
      {entry.date && (
        <div className={`flex-shrink-0 flex flex-col text-[10px] font-semibold tabular-nums ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
          <span>{entry.date.slice(0, 5)}</span>
          {entry.end_time && (
            <span className={`font-normal ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
              {formatTime(entry.end_time)}
            </span>
          )}
        </div>
      )}

      {/* Narrative */}
      <div className="flex-1 min-w-0 mr-3">
        <span className="whitespace-normal break-words leading-snug">
          {entry.description}
        </span>
      </div>

      {/* Units badge — matches EntriesTable .unitsBadge */}
      <div
        className="flex-shrink-0 text-[13px] px-2.5 py-1 min-w-[50px] text-center"
        style={{
          fontWeight: 525,
          borderRadius: "7px",
          backgroundColor: darkMode
            ? "var(--card)"
            : "var(--brand-dark-green)",
          color: darkMode
            ? "var(--foreground)"
            : "var(--background)",
          border: darkMode
            ? "none"
            : "1px solid var(--background)",
        }}
      >
          {entry.duration.toFixed(2).endsWith("0")
            ? entry.duration.toFixed(1)
            : entry.duration.toFixed(2)}
      </div>
    </div>
  );
}

export const CombineDialog: React.FC<CombineDialogProps> = ({
  open,
  onOpenChange,
  entries,
  matters,
  onConfirm,
  onCancel,
}) => {
  const { darkMode } = useDarkMode();
  const getIcon = useAppIcon(darkMode);
  // Ordered entry IDs — first one is the main entry
  const [orderedIds, setOrderedIds] = useState<number[]>([]);
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);

  // Reset state when dialog closes so next open starts fresh
  useEffect(() => {
    if (!open) {
      setOrderedIds([]);
      setSelectedMatterId(null);
      return;
    }

    // Initialize when dialog opens with entries
    setOrderedIds(entries.map((e) => e.id));

    const entryMatterIds = entries.map((e) => e.matter_id).filter(Boolean) as string[];
    const uniqueMatters = Array.from(new Set(entryMatterIds));
    if (uniqueMatters.length === 1) {
      setSelectedMatterId(uniqueMatters[0]);
    } else {
      setSelectedMatterId(null);
    }
  }, [open, entries]);

  const [isCombining, setIsCombining] = useState(false);
  const mainEntryId = orderedIds[0] ?? null;

  // Combined units total
  const combinedTotal = useMemo(
    () => entries.reduce((sum, e) => sum + (e.duration || 0), 0),
    [entries]
  );

  // Matter source: use API matters if available, otherwise derive from the entries
  const sourceMattersList = useMemo((): Matter[] => {
    if (matters.length > 0) return matters;
    const seen = new Set<string>();
    const result: Matter[] = [];
    for (const e of entries) {
      if (e.matter_id && !seen.has(e.matter_id)) {
        seen.add(e.matter_id);
        result.push({
          matter: e.matter || e.matter_name || "",
          matter_id: e.matter_id,
          client: e.client || "",
          description: "",
          task_group: "",
          activity_group: "",
        });
      }
    }
    return result;
  }, [matters, entries]);

  // Search field extractors for ComboBox (same fields as EntryForm matter picker)
  const matterFields: ((item: Matter) => SearchableField)[] = useMemo(
    () => [
      (m: Matter) => ({ value: m.matter, weight: 1.5 }),
      (m: Matter) => ({ value: m.client, weight: 1.2 }),
      (m: Matter) => ({ value: m.description, weight: 1.0 }),
    ],
    [],
  );

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedIds((prev) => {
        const oldIndex = prev.indexOf(active.id as number);
        const newIndex = prev.indexOf(over.id as number);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const promoteToMain = (id: number) => {
    setOrderedIds((prev) => {
      const without = prev.filter((i) => i !== id);
      return [id, ...without];
    });
  };

  const handleConfirm = async () => {
    if (!selectedMatterId || mainEntryId === null) return;
    setIsCombining(true);
    try {
      await onConfirm(orderedIds, selectedMatterId, mainEntryId);
    } finally {
      setIsCombining(false);
    }
  };

  // Build ordered entries from orderedIds
  const orderedEntries = useMemo(
    () => orderedIds.map((id) => entries.find((e) => e.id === id)).filter(Boolean) as Entry[],
    [orderedIds, entries]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          // Focus the dialog content itself so the focus trap activates
          // without opening the ComboBox dropdown
          (e.target as HTMLElement).focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Combine Entries</DialogTitle>
          <DialogDescription>
            Select a matter and drag to reorder. The top entry drives the combined narrative.
          </DialogDescription>
        </DialogHeader>

        {/* Matter selector */}
        <div className="flex flex-col w-full gap-1.5">
          <ComboBox
            items={sourceMattersList}
            value={selectedMatterId}
            onChange={(matterId) => setSelectedMatterId(matterId)}
            fields={matterFields}
            getKey={(m) => m.matter_id}
            getDisplayLabel={(m) =>
              `${m.matter.trim()} | ${m.description.trim()} - ${m.client.trim()}`
            }
            placeholder="Search matter..."
            error={!selectedMatterId ? "Matter is required" : undefined}
          />
        </div>

        {/* Sortable entry list */}
        <div
          className="rounded-md border border-border overflow-y-auto overflow-x-hidden"
          style={{ maxHeight: "340px" }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedIds}
              strategy={verticalListSortingStrategy}
            >
              {orderedEntries.map((entry, index) => (
                <SortableEntryRow
                  key={entry.id}
                  entry={entry}
                  isMain={index === 0}
                  onPromote={() => promoteToMain(entry.id)}
                  darkMode={darkMode}
                  getIcon={getIcon}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        {/* Combined total + actions */}
        <div className={`border-t pt-3 ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
          <div className={`text-sm font-medium mb-3 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            Combined total:{" "}
            <span className={darkMode ? "text-white" : "text-gray-900"}>
              {combinedTotal.toFixed(1)} hrs
            </span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedMatterId || mainEntryId === null}
              loading={isCombining}
              className={!selectedMatterId || mainEntryId === null ? "opacity-50 cursor-not-allowed" : ""}
              style={{ backgroundColor: "var(--brand-dark-green)", color: "white" }}
            >
              Combine
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
